import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fleetApi } from '../api/fleet';
import { refusalMessage } from '../api/errors';
import { vehicles as vehiclesApi } from '../api/vehicles';
import { rememberFleetRole } from '../lib/fleetRole';
import { useVehicleTypes } from '../lib/useVehicleTypes';
import './driverCompanyFleet.css';

/*
 * A rider's delivery company on InGo's API (InGo ADR 0032; Rio, 2 Oct 2026:
 * "we can rebuild that too to make it smoother for them"). To its owner, the
 * whole fleet: each rider's standing, online or on a job, their live delivery
 * in full (Rio: "Everything"), and the period's deliveries and money as the
 * server read them from the commission lines; adding a new rider, who chooses
 * their own password from an emailed link; taking one out. To one of its
 * riders, only whose fleet it is. To anyone else, the way to open one.
 *
 * The old screen had the owner type each rider's password, took 15 % off in
 * the browser, and opened to any rider of the company.
 */

const PERIODS = [
  ['today', 'Today'],
  ['week', 'This week'],
  ['month', 'This month'],
];

// The vehicle is one of the office's types (`useVehicleTypes`), the first until the owner picks one.
const EMPTY = { first_name: '', last_name: '', email: '', phone: '', vehicle_type: '', vehicle_make: '', vehicle_model: '', vehicle_plate: '', vehicle_colour: '' };

function moneyOf(minor, currency) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format((Number(minor) || 0) / 100);
}

/** "$12.00", or one per currency, or a dash when there is none. */
function sums(money, key) {
  const entries = Object.entries(money || {});

  return entries.length === 0 ? moneyOf(0) : entries.map(([currency, figures]) => moneyOf(figures[key], currency)).join(' · ');
}

function Contact({ label, pin }) {
  if (!pin) return null;

  return (
    <div className="dcf-muted">
      <strong>{label}:</strong> {pin.address || '—'}
      {pin.contact_name ? ` · ${pin.contact_name}` : ''}
      {pin.contact_phone ? (
        <>
          {' · '}
          <a href={`tel:${pin.contact_phone}`}>{pin.contact_phone}</a>
        </>
      ) : null}
    </div>
  );
}

function LiveDelivery({ live }) {
  return (
    <div className="dcf-live" aria-label={`Delivery ${live.reference}`}>
      <p>
        <strong>{live.reference}</strong> · {live.status_label} · {moneyOf(live.total_minor, live.currency)}
        {live.payment_method ? ` ${live.payment_method}` : ''}
      </p>
      <Contact label="Pickup" pin={live.pickup} />
      <Contact label="Drop-off" pin={live.dropoff} />
    </div>
  );
}

function OpenFleet({ fleet, onOpened }) {
  const [name, setName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setProblem('');
    setBusy(true);
    try {
      await fleet.open(name.trim(), tradingName.trim() || null);
      rememberFleetRole('owner');
      setBusy(false);
      // Last: the page reads the fleet and this form goes.
      onOpened();
    } catch (error) {
      setProblem(refusalMessage(error));
      setBusy(false);
    }
  };

  return (
    <form className="dcf-card dcf-form" onSubmit={submit}>
      <h2>Run a delivery company</h2>
      <p className="dcf-muted">Open it here, then add your riders. You can still ride yourself.</p>
      <label>
        Company name
        <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
      </label>
      <label>
        Trading name (optional)
        <input value={tradingName} onChange={(e) => setTradingName(e.target.value)} maxLength={120} />
      </label>
      {problem ? (
        <p className="dcf-alert" role="alert">
          {problem}
        </p>
      ) : null}
      <button type="submit" className="dcf-btn dcf-btn--primary" disabled={busy}>
        {busy ? 'Opening…' : 'Open my company'}
      </button>
    </form>
  );
}

function AddRider({ fleet, onAdded, vehicleTypes }) {
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));
  const { vehicles, loading: vehiclesLoading, failed: vehiclesFailed, retry: retryVehicles } = useVehicleTypes(vehicleTypes);
  const vehicleType = vehicles.some((v) => v.key === draft.vehicle_type) ? draft.vehicle_type : vehicles[0]?.key ?? '';

  const submit = async (e) => {
    e.preventDefault();
    setProblem('');
    if (!vehicleType) {
      setProblem('Choose their vehicle.');
      return;
    }
    setBusy(true);
    try {
      const added = await fleet.addRider({ ...draft, vehicle_type: vehicleType });
      setDraft(EMPTY);
      setBusy(false);
      // Last: the page closes this form.
      onAdded(added.message);
    } catch (error) {
      setProblem(refusalMessage(error));
      setBusy(false);
    }
  };

  return (
    <form className="dcf-card dcf-form" onSubmit={submit} aria-label="Add a rider">
      <h2>Add a rider</h2>
      <p className="dcf-muted">
        They get an email to choose their own password, then send their papers from the app. The office checks them
        before their first delivery.
      </p>
      <div className="dcf-grid2">
        <label>
          First name
          <input value={draft.first_name} onChange={set('first_name')} required />
        </label>
        <label>
          Last name
          <input value={draft.last_name} onChange={set('last_name')} />
        </label>
      </div>
      <label>
        Their email
        <input type="email" value={draft.email} onChange={set('email')} autoComplete="off" required />
      </label>
      <label>
        Their phone
        <input value={draft.phone} onChange={set('phone')} inputMode="tel" required />
      </label>
      <label>
        Vehicle
        <select value={vehicleType} onChange={set('vehicle_type')} disabled={vehicles.length === 0}>
          {vehicles.length === 0 ? <option value="">{vehiclesLoading ? 'Finding InGo’s vehicles…' : 'No vehicles to choose'}</option> : null}
          {vehicles.map((v) => (
            <option key={v.key} value={v.key}>
              {v.label}
            </option>
          ))}
        </select>
      </label>
      {vehiclesFailed ? (
        <p className="dcf-muted" role="alert">
          InGo&apos;s vehicles could not be loaded.{' '}
          <button type="button" className="dcf-link" onClick={retryVehicles}>
            Try again
          </button>
        </p>
      ) : null}
      <div className="dcf-grid2">
        <label>
          Make
          <input value={draft.vehicle_make} onChange={set('vehicle_make')} required />
        </label>
        <label>
          Model
          <input value={draft.vehicle_model} onChange={set('vehicle_model')} required />
        </label>
      </div>
      <div className="dcf-grid2">
        <label>
          Plate
          <input value={draft.vehicle_plate} onChange={set('vehicle_plate')} required />
        </label>
        <label>
          Colour
          <input value={draft.vehicle_colour} onChange={set('vehicle_colour')} required />
        </label>
      </div>
      {problem ? (
        <p className="dcf-alert" role="alert">
          {problem}
        </p>
      ) : null}
      <button type="submit" className="dcf-btn dcf-btn--primary" disabled={busy}>
        {busy ? 'Adding…' : 'Add rider'}
      </button>
    </form>
  );
}

export default function DriverCompanyFleetPage({ fleet = fleetApi, vehicleTypes = vehiclesApi }) {
  const [period, setPeriod] = useState('week');
  const [load, setLoad] = useState({ state: 'loading' });
  const [adding, setAdding] = useState(false);
  const [said, setSaid] = useState('');
  const [problem, setProblem] = useState('');

  const read = useCallback(() => {
    fleet.me(period).then(
      (view) => {
        rememberFleetRole(view?.role ?? null);
        setLoad({ state: 'ready', view });
      },
      (error) => setLoad({ state: 'failed', message: refusalMessage(error) }),
    );
  }, [fleet, period]);

  useEffect(() => {
    read();
  }, [read]);

  const takeOut = async (rider) => {
    if (!window.confirm(`Take ${rider.name} out of your fleet? They stay a rider, on their own.`)) return;
    setProblem('');
    try {
      setSaid(await fleet.takeOut(rider.id));
      read();
    } catch (error) {
      setProblem(refusalMessage(error));
    }
  };

  if (load.state === 'loading') {
    return (
      <div className="dcf-page">
        <p className="dcf-muted" role="status">
          Loading fleet…
        </p>
      </div>
    );
  }

  if (load.state === 'failed') {
    return (
      <div className="dcf-page">
        <p className="dcf-alert" role="alert">
          {load.message}
        </p>
      </div>
    );
  }

  const { view } = load;

  if (view.role === null) {
    return (
      <div className="dcf-page">
        <h1>Fleet</h1>
        <p className="dcf-muted">You are not in a delivery company.</p>
        <OpenFleet fleet={fleet} onOpened={read} />
      </div>
    );
  }

  if (view.role === 'rider') {
    return (
      <div className="dcf-page">
        <p className="dcf-eyebrow">Delivery company</p>
        <h1>{view.fleet.name}</h1>
        <p className="dcf-muted">You ride for {view.fleet.name}, owned by {view.owner.name}. Your own figures are on Earnings.</p>
        <p className="dcf-muted">
          To leave this fleet, use your <Link to="/driver/profile">profile</Link>.
        </p>
      </div>
    );
  }

  const { totals, riders } = view;
  const money = totals.money;

  return (
    <div className="dcf-page">
      <header className="dcf-head">
        <div>
          <p className="dcf-eyebrow">Delivery company</p>
          <h1>{view.fleet.name}</h1>
          <p className="dcf-muted">{view.fleet.trading_name ? `Trading as ${view.fleet.trading_name}` : 'Your riders and their work'}</p>
        </div>
        <button type="button" className="dcf-btn" onClick={() => setAdding((on) => !on)}>
          {adding ? 'Close' : 'Add rider'}
        </button>
      </header>

      <div className="dcf-periods" role="group" aria-label="Period">
        {PERIODS.map(([key, label]) => (
          <button key={key} type="button" className={key === period ? 'dcf-btn dcf-btn--primary' : 'dcf-btn'} aria-pressed={key === period} onClick={() => setPeriod(key)}>
            {label}
          </button>
        ))}
      </div>

      {said ? (
        <p className="dcf-ok" role="status">
          {said}
        </p>
      ) : null}
      {problem ? (
        <p className="dcf-alert" role="alert">
          {problem}
        </p>
      ) : null}

      <section className="dcf-stats" aria-label="Fleet summary">
        <article>
          <p className="dcf-stat-label">Riders</p>
          <p className="dcf-stat-value">{totals.riders}</p>
        </article>
        <article>
          <p className="dcf-stat-label">Online</p>
          <p className="dcf-stat-value">{totals.online}</p>
        </article>
        <article>
          <p className="dcf-stat-label">On a job</p>
          <p className="dcf-stat-value">{totals.on_a_job}</p>
        </article>
        <article>
          <p className="dcf-stat-label">Delivered</p>
          <p className="dcf-stat-value">{totals.completed}</p>
        </article>
      </section>
      <p className="dcf-muted" aria-label="Fleet money">
        {view.period.label}: fares {sums(money, 'fare_minor')} · InGo&apos;s commission {sums(money, 'platform_minor')} · your riders kept{' '}
        {sums(money, 'rider_minor')}
      </p>

      {adding ? (
        <AddRider
          fleet={fleet}
          vehicleTypes={vehicleTypes}
          onAdded={(message) => {
            setSaid(message);
            setAdding(false);
            read();
          }}
        />
      ) : null}

      <section className="dcf-card">
        <h2>Your riders</h2>
        {riders.length <= 1 ? <p className="dcf-muted">No riders yet. Add your first rider to start.</p> : null}
        <ul className="dcf-bike-list">
          {riders.map((rider) => (
            <li key={rider.id} className="dcf-bike" aria-label={rider.name}>
              <div className="dcf-bike__top">
                <strong>
                  {rider.name}
                  {rider.role === 'owner' ? ' (you)' : ''}
                </strong>
                <span className="dcf-pill">{rider.on_a_job ? 'On a job' : rider.online ? 'Online' : 'Offline'}</span>
              </div>
              <p className="dcf-muted">
                {rider.state_label}
                {rider.may_work ? '' : ' · cannot take deliveries yet'} · {rider.vehicle}
                {rider.plate ? ` · ${rider.plate}` : ''}
              </p>
              {rider.phone ? (
                <p className="dcf-muted">
                  <a href={`tel:${rider.phone}`}>{rider.phone}</a>
                </p>
              ) : null}
              {rider.live ? <LiveDelivery live={rider.live} /> : null}
              <div className="dcf-bike__stats">
                <span>Delivered: {rider.completed}</span>
                <span>Kept: {sums(rider.money, 'rider_minor')}</span>
              </div>
              {rider.role === 'rider' ? (
                <button type="button" className="dcf-btn" onClick={() => takeOut(rider)}>
                  Take out of fleet
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
